import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { AdminActionsLanguage } from '../../../../../i18n'
import { AcdStatus, ActionField, AdminAction, ForwardStatus, MonitorType } from './Types'

export const getActions = (language: CLanguageHandler): Record<AdminAction, ActionField[]> => {
    const deviceField: ActionField = {
        id: 'DeviceMain',
        label: language.get(AdminActionsLanguage.Field_Label_DeviceMain),
        type: 'string'
    }

    const destinationField: ActionField = {
        id: 'Destination',
        label: language.get(AdminActionsLanguage.Field_Label_Destination),
        type: 'string'
    }

    const activeCallIdField: ActionField = {
        id: 'CallIDMain',
        label: language.get(AdminActionsLanguage.Field_Label_CallIDMain),
        type: 'string'
    }

    const destinationAutoPrefixField: ActionField = {
        id: 'DestinationAutoPrefix',
        label: language.get(AdminActionsLanguage.Field_Label_DestinationAutoPrefix),
        type: 'boolean'
    }

    const otherCallIDField: ActionField = {
        id: 'CallIDHeld',
        label: language.get(AdminActionsLanguage.Field_Label_CallIDHeld),
        type: 'string'
    }

    return {
        [AdminAction.MakeCall]: [deviceField, destinationField, destinationAutoPrefixField],
        [AdminAction.ConsultationCall]: [deviceField, destinationField, activeCallIdField, destinationAutoPrefixField],
        [AdminAction.DialDTMFTone]: [
            deviceField,
            activeCallIdField,
            {
                id: 'Destination',
                label: language.get(AdminActionsLanguage.Field_Label_DTMFTones),
                type: 'string'
            }
        ],
        [AdminAction.ClearCallViaCallControl]: [activeCallIdField],
        [AdminAction.ClearCallViaCallModel]: [activeCallIdField],
        [AdminAction.ClearConnection]: [deviceField, activeCallIdField],
        [AdminAction.AnswerCall]: [deviceField, activeCallIdField],
        [AdminAction.HoldCall]: [deviceField, activeCallIdField],
        [AdminAction.ParkCall]: [
            deviceField,
            activeCallIdField,
            {
                id: 'ParkLocation',
                label: language.get(AdminActionsLanguage.Field_Label_ParkLocation),
                type: 'string'
            }
        ],
        [AdminAction.RetrieveCall]: [deviceField, activeCallIdField],
        [AdminAction.AlternateCall]: [deviceField, activeCallIdField, otherCallIDField],
        [AdminAction.PickupCall]: [
            deviceField,
            activeCallIdField,
            {
                id: 'DeviceToPickupAt',
                label: language.get(AdminActionsLanguage.Field_Label_DeviceToPickupAt),
                type: 'string'
            },
            {
                id: 'CallIDHeld',
                label: 'Call Parked',
                type: 'boolean'
            }
        ],
        [AdminAction.TransferOneStep]: [deviceField, destinationField, activeCallIdField, destinationAutoPrefixField],
        [AdminAction.TransferComplete]: [deviceField, activeCallIdField, otherCallIDField],
        [AdminAction.DivertDeflectCall]: [deviceField, destinationField, activeCallIdField, destinationAutoPrefixField],
        [AdminAction.MonitorConnection]: [
            deviceField,
            destinationField,
            activeCallIdField,
            {
                id: 'MonitorType',
                label: language.get(AdminActionsLanguage.Field_Label_MonitorType),
                type: 'list',
                options: [
                    {
                        value: MonitorType.SilentMonitor,
                        label: 'Silent Monitor'
                    },
                    {
                        value: MonitorType.ForcedIntrude,
                        label: 'Forced Intrude'
                    }
                ]
            }
        ],
        [AdminAction.RecordCall]: [deviceField, destinationField, activeCallIdField],
        [AdminAction.RegisterCallback]: [deviceField, destinationField],
        [AdminAction.ConferenceJoin]: [deviceField, activeCallIdField, otherCallIDField],
        [AdminAction.MonitorStart]: [
            deviceField,
            {
                id: 'Password',
                label: language.get(AdminActionsLanguage.Field_Label_Password),
                type: 'password'
            },
            {
                id: 'UseSecurePassword',
                label: language.get(AdminActionsLanguage.Field_Label_UseSecurePassword),
                type: 'boolean'
            }
        ],
        [AdminAction.MonitorStop]: [
            deviceField,
            {
                id: 'MonitorCrossRefID',
                label: language.get(AdminActionsLanguage.Field_Label_MonitorCrossRefID),
                type: 'string'
            }
        ],
        [AdminAction.SetDNDStatus]: [
            deviceField,
            {
                id: 'IsDND',
                label: language.get(AdminActionsLanguage.Field_Label_IsDND),
                type: 'boolean'
            }
        ],
        [AdminAction.SetACDStat]: [
            deviceField,
            {
                id: 'AgentID',
                label: language.get(AdminActionsLanguage.Field_Label_AgentID),
                type: 'string'
            },
            {
                id: 'ACDGroup',
                label: language.get(AdminActionsLanguage.Field_Label_ACDGroup),
                type: 'string'
            },
            {
                id: 'Password',
                label: language.get(AdminActionsLanguage.Field_Label_Password),
                type: 'password'
            },
            {
                id: 'ACDStateReasonTextID',
                label: language.get(AdminActionsLanguage.Field_Label_ACDStateReasonTextID),
                type: 'string' // value is always 1
            },
            {
                id: 'ACDStatus',
                label: language.get(AdminActionsLanguage.Field_Label_ACDStatus),
                type: 'list',
                options: [
                    { label: language.get(AdminActionsLanguage.AcdOption_SignIn), value: AcdStatus.SignIn },
                    { label: language.get(AdminActionsLanguage.AcdOption_SignOut), value: AcdStatus.SignOut },
                    { label: language.get(AdminActionsLanguage.AcdOption_Available), value: AcdStatus.Available },
                    { label: language.get(AdminActionsLanguage.AcdOption_NotAvailable), value: AcdStatus.NotAvailable },
                    { label: language.get(AdminActionsLanguage.AcdOption_AlertingAcd), value: AcdStatus.AlertingAcd },
                    {
                        label: language.get(AdminActionsLanguage.AcdOption_AlertingNonAcd),
                        value: AcdStatus.AlertingNonAcd
                    },
                    { label: language.get(AdminActionsLanguage.AcdOption_BusyAcd), value: AcdStatus.BusyAcd },
                    { label: language.get(AdminActionsLanguage.AcdOption_BusyNonAcd), value: AcdStatus.BusyNonAcd },
                    {
                        label: language.get(AdminActionsLanguage.AcdOption_BusySupervisor),
                        value: AcdStatus.BusySupervisor
                    },
                    { label: language.get(AdminActionsLanguage.AcdOption_AcdDnd), value: AcdStatus.AcdDnd },
                    { label: language.get(AdminActionsLanguage.AcdOption_WrapUp), value: AcdStatus.WrapUp }
                ]
            }
        ],
        [AdminAction.SetForwardStatus]: [
            deviceField,
            destinationField,
            {
                id: 'ForwardMode',
                label: language.get(AdminActionsLanguage.Field_Label_ForwardMode),
                type: 'list',
                options: [
                    { label: language.get(AdminActionsLanguage.ForwardOption_None), value: ForwardStatus.None },
                    {
                        label: language.get(AdminActionsLanguage.ForwardOption_Immediate),
                        value: ForwardStatus.Immediate
                    },
                    { label: language.get(AdminActionsLanguage.ForwardOption_OnBusy), value: ForwardStatus.OnBusy },
                    { label: language.get(AdminActionsLanguage.ForwardOption_NoAnswer), value: ForwardStatus.NoAnswer },
                    { label: language.get(AdminActionsLanguage.ForwardOption_OnDnd), value: ForwardStatus.OnDND },
                    { label: language.get(AdminActionsLanguage.ForwardOption_OnOos), value: ForwardStatus.OnOOS }
                ]
            }
        ],
        [AdminAction.SetAccountCode]: [
            deviceField,
            activeCallIdField,
            {
                id: 'AccountCode',
                label: language.get(AdminActionsLanguage.Field_Label_AccountCode),
                type: 'string'
            }
        ],
        [AdminAction.SetEntityPassword]: [
            deviceField,
            {
                id: 'PasswordOld',
                label: language.get(AdminActionsLanguage.Field_Label_PasswordOld),
                type: 'password'
            },
            {
                id: 'PasswordNew',
                label: language.get(AdminActionsLanguage.Field_Label_PasswordNew),
                type: 'password'
            }
        ],
        [AdminAction.QueryDNDStat]: [deviceField],
        [AdminAction.QueryACDStat]: [deviceField],
        [AdminAction.QueryForwardStatus]: [deviceField],
        [AdminAction.SnapshotCall]: [deviceField, activeCallIdField],
        [AdminAction.SnapshotDevice]: [deviceField]
    }
}
